import { supabase } from '../supabase';
import { useQuery, useMutation } from '@tanstack/react-query';
import { ScheduleShift, ScheduleShiftType } from '@shiftly/shared/src/schema';
import { DateTime } from 'luxon';
import SupabaseShiftLoggingService from '../logging/supabaseShiftLoggingService';

export interface ScheduleShiftFilter {
  field: keyof ScheduleShift | string;
  operator: 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte' | 'in' | 'not-in' | 'is';
  value: any;
}

export interface ScheduleShiftSort {
  field: keyof ScheduleShift | string;
  direction: 'asc' | 'desc';
}

export interface ScheduleShiftParams {
  filters?: ScheduleShiftFilter[];
  sort?: ScheduleShiftSort[];
  enabled?: boolean;
}

const normalizeDateTo12PM = (date: DateTime): DateTime => {
  return date.set({ hour: 12, minute: 0, second: 0, millisecond: 0 });
};

export function convertDateFields(data: any): ScheduleShift {
  return {
    ...data,
    date: DateTime.fromISO(data.date, { zone: 'UTC' }),
    completed_at: data.completed_at ? DateTime.fromISO(data.completed_at, { zone: 'UTC' }) : null,
    created_at: DateTime.fromISO(data.created_at, { zone: 'UTC' }),
    updated_at: DateTime.fromISO(data.updated_at, { zone: 'UTC' })
  } as ScheduleShift;
}

class SupabaseScheduleShiftService {
  async getScheduleShifts({
    filters = [],
    sort = []
  }: ScheduleShiftParams): Promise<ScheduleShift[]> {
    try {
      const inFilters = filters.filter(f => f.operator === 'in');
      const largeInFilter = inFilters.find(f => Array.isArray(f.value) && f.value.length > 10);

      if (largeInFilter && Array.isArray(largeInFilter.value)) {
        // Optimized batching for large 'in' queries
        const CHUNK_SIZE = 100;
        const MAX_CONCURRENT_QUERIES = 10;

        const values = largeInFilter.value;
        const chunks = Array.from(
          { length: Math.ceil(values.length / CHUNK_SIZE) },
          (_, i) => values.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE)
        );

        const processChunk = async (chunkValues: any[]): Promise<ScheduleShift[]> => {
          // Create filters for this chunk
          const chunkFilters = filters.map(f =>
            f === largeInFilter ? { ...f, value: chunkValues } : f
          );

          // Get total count first to optimize pagination
          let countQuery = supabase.from('schedule_shifts').select('*', { count: 'exact', head: true });
          chunkFilters.forEach(filter => {
            switch (filter.operator) {
              case 'eq': countQuery = countQuery.eq(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value); break;
              case 'neq': countQuery = countQuery.neq(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value); break;
              case 'gt': countQuery = countQuery.gt(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value); break;
              case 'gte': countQuery = countQuery.gte(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value); break;
              case 'lt': countQuery = countQuery.lt(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value); break;
              case 'lte': countQuery = countQuery.lte(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value); break;
              case 'in': countQuery = countQuery.in(filter.field, filter.value); break;
              case 'not-in': countQuery = countQuery.not(filter.field, 'in', `(${filter.value.map((v: any) => JSON.stringify(v)).join(',')})`); break;
              case 'is': countQuery = countQuery.is(filter.field, filter.value); break;
            }
          });

          const { count } = await countQuery;
          if (!count || count === 0) return [];

          // Calculate optimal page count for parallel requests
          const totalPages = Math.ceil(count / SUPABASE_PAGE_SIZE);
          const pagePromises: Promise<ScheduleShift[]>[] = [];

          // Create all page requests in parallel
          for (let page = 0; page < totalPages; page++) {
            const pagePromise = (async (offset: number): Promise<ScheduleShift[]> => {
              let query = supabase.from('schedule_shifts').select('*');

              // Apply filters
              chunkFilters.forEach(filter => {
                switch (filter.operator) {
                  case 'eq':
                    query = query.eq(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value);
                    break;
                  case 'neq':
                    query = query.neq(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value);
                    break;
                  case 'gt':
                    query = query.gt(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value);
                    break;
                  case 'gte':
                    query = query.gte(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value);
                    break;
                  case 'lt':
                    query = query.lt(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value);
                    break;
                  case 'lte':
                    query = query.lte(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value);
                    break;
                  case 'in':
                    query = query.in(filter.field, filter.value);
                    break;
                  case 'not-in':
                    query = query.not(filter.field, 'in', `(${filter.value.map((v: any) => JSON.stringify(v)).join(',')})`);
                    break;
                  case 'is':
                    query = query.is(filter.field, filter.value);
                    break;
                }
              });

              // Apply sorting with stable secondary sort
              if (sort.length > 0) {
                sort.forEach(sortOption => {
                  query = query.order(sortOption.field, { ascending: sortOption.direction === 'asc' });
                });
                // Add stable secondary sort if not already sorting by id
                if (!sort.some(s => s.field === 'id')) {
                  query = query.order('id', { ascending: true });
                }
              } else {
                query = query.order('date', { ascending: true }).order('id', { ascending: true });
              }

              // Add pagination
              query = query.range(offset, offset + SUPABASE_PAGE_SIZE - 1);

              const { data, error } = await query;

              if (error) throw error;

              return (data || []) as ScheduleShift[];
            })(page * SUPABASE_PAGE_SIZE);

            pagePromises.push(pagePromise);
          }

          // Execute all page requests in parallel
          const pageResults = await Promise.all(pagePromises);
          return pageResults.flat();
        };

        // Process chunks with optimized concurrency
        const results: ScheduleShift[] = [];

        for (let i = 0; i < chunks.length; i += MAX_CONCURRENT_QUERIES) {
          const currentBatch = chunks.slice(i, i + MAX_CONCURRENT_QUERIES);
          const batchPromises = currentBatch.map(processChunk);

          const batchResults = await Promise.all(batchPromises);
          results.push(...batchResults.flat());
        }

        return results.map((shift: ScheduleShift) => convertDateFields(shift));
      }

      // Standard pagination for normal queries
      const SUPABASE_PAGE_SIZE = 1000;
      const MAX_CONCURRENT_PAGES = 10;

      // Get total count first
      let countQuery = supabase.from('schedule_shifts').select('*', { count: 'exact', head: true });
      filters.forEach(filter => {
        switch (filter.operator) {
          case 'eq': countQuery = countQuery.eq(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value); break;
          case 'neq': countQuery = countQuery.neq(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value); break;
          case 'gt': countQuery = countQuery.gt(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value); break;
          case 'gte': countQuery = countQuery.gte(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value); break;
          case 'lt': countQuery = countQuery.lt(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value); break;
          case 'lte': countQuery = countQuery.lte(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value); break;
          case 'in': countQuery = countQuery.in(filter.field, filter.value); break;
          case 'not-in': countQuery = countQuery.not(filter.field, 'in', `(${filter.value.map((v: any) => JSON.stringify(v)).join(',')})`); break;
          case 'is': countQuery = countQuery.is(filter.field, filter.value); break;
        }
      });

      const { count } = await countQuery;
      if (!count || count === 0) return [];

      const totalPages = Math.ceil(count / SUPABASE_PAGE_SIZE);

      // For large datasets, use parallel pagination
      if (totalPages > 5) {
        const allResults: ScheduleShift[] = [];

        for (let batchStart = 0; batchStart < totalPages; batchStart += MAX_CONCURRENT_PAGES) {
          const batchEnd = Math.min(batchStart + MAX_CONCURRENT_PAGES, totalPages);
          const pagePromises: Promise<ScheduleShift[]>[] = [];

          for (let page = batchStart; page < batchEnd; page++) {
            const pagePromise = (async (offset: number): Promise<ScheduleShift[]> => {
              let query = supabase.from('schedule_shifts').select('*');

              // Apply filters
              filters.forEach(filter => {
                switch (filter.operator) {
                  case 'eq':
                    query = query.eq(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value);
                    break;
                  case 'neq':
                    query = query.neq(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value);
                    break;
                  case 'gt':
                    query = query.gt(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value);
                    break;
                  case 'gte':
                    query = query.gte(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value);
                    break;
                  case 'lt':
                    query = query.lt(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value);
                    break;
                  case 'lte':
                    query = query.lte(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value);
                    break;
                  case 'in':
                    query = query.in(filter.field, filter.value);
                    break;
                  case 'not-in':
                    query = query.not(filter.field, 'in', `(${filter.value.map((v: any) => JSON.stringify(v)).join(',')})`);
                    break;
                  case 'is':
                    query = query.is(filter.field, filter.value);
                    break;
                }
              });

              // Apply sorting with stable secondary sort
              if (sort.length > 0) {
                sort.forEach(sortOption => {
                  query = query.order(sortOption.field, { ascending: sortOption.direction === 'asc' });
                });
                // Add stable secondary sort if not already sorting by id
                if (!sort.some(s => s.field === 'id')) {
                  query = query.order('id', { ascending: true });
                }
              } else {
                query = query.order('date', { ascending: true }).order('id', { ascending: true });
              }

              // Add pagination
              query = query.range(offset, offset + SUPABASE_PAGE_SIZE - 1);

              const { data, error } = await query;
              if (error) throw error;

              return (data || []) as ScheduleShift[];
            })(page * SUPABASE_PAGE_SIZE);

            pagePromises.push(pagePromise);
          }

          // Execute batch in parallel
          const batchResults = await Promise.all(pagePromises);
          allResults.push(...batchResults.flat());
        }

        return allResults.map((shift: ScheduleShift) => convertDateFields(shift));
      } else {
        // Use sequential pagination for small datasets
        const allResults: ScheduleShift[] = [];
        let hasMore = true;
        let offset = 0;

        while (hasMore) {
          let query = supabase.from('schedule_shifts').select('*');

          // Apply filters
          filters.forEach(filter => {
            switch (filter.operator) {
              case 'eq':
                query = query.eq(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value);
                break;
              case 'neq':
                query = query.neq(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value);
                break;
              case 'gt':
                query = query.gt(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value);
                break;
              case 'gte':
                query = query.gte(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value);
                break;
              case 'lt':
                query = query.lt(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value);
                break;
              case 'lte':
                query = query.lte(filter.field, DateTime.isDateTime(filter.value) ? filter.value.toISO() : filter.value);
                break;
              case 'in':
                query = query.in(filter.field, filter.value);
                break;
              case 'not-in':
                query = query.not(filter.field, 'in', `(${filter.value.map((v: any) => JSON.stringify(v)).join(',')})`);
                break;
              case 'is':
                query = query.is(filter.field, filter.value);
                break;
            }
          });

          // Apply sorting with stable secondary sort
          if (sort.length > 0) {
            sort.forEach(sortOption => {
              query = query.order(sortOption.field, { ascending: sortOption.direction === 'asc' });
            });
            // Add stable secondary sort if not already sorting by id
            if (!sort.some(s => s.field === 'id')) {
              query = query.order('id', { ascending: true });
            }
          } else {
            query = query.order('date', { ascending: true }).order('id', { ascending: true });
          }

          // Add pagination
          query = query.range(offset, offset + SUPABASE_PAGE_SIZE - 1);

          const { data, error } = await query;

          if (error) {
            console.error('Error fetching schedule shifts page:', error);
            throw error;
          }

          const shifts = (data || []) as ScheduleShift[];
          allResults.push(...shifts);

          // Check if we got fewer results than requested (end of data)
          hasMore = shifts.length === SUPABASE_PAGE_SIZE;
          offset += SUPABASE_PAGE_SIZE;
        }

        return allResults.map((shift: ScheduleShift) => convertDateFields(shift));
      }
    } catch (error) {
      console.error('Error in getScheduleShifts:', error);
      return [];
    }
  }

  async createScheduleShift(shift: Omit<ScheduleShift, 'id' | 'created_at' | 'updated_at'>, performed_by?: string): Promise<string> {
    try {
      const now = DateTime.utc();
      const normalizedDate = normalizeDateTo12PM(shift.date);

      const { data, error } = await supabase
        .from('schedule_shifts')
        .insert({
          user_id: shift.user_id,
          model_id: shift.model_id,
          type: shift.type,
          date: normalizedDate.toISO(),
          is_ot: shift.is_ot ?? false,
          completed_at: shift.completed_at?.toISO(),
          created_at: now.toISO(),
          updated_at: now.toISO()
        })
        .select('id')
        .single();

      if (error) {
        console.error('Error creating schedule shift:', error);
        throw error;
      }

      // Get current user for logging
      const currentUser = performed_by || (await supabase.auth.getUser()).data.user?.id;
      
      // Log the schedule shift creation if we have a user
      if (currentUser) {
        try {
          const createdShift: ScheduleShift = {
            ...shift,
            id: data.id,
            created_at: now,
            updated_at: now,
          };
          await SupabaseShiftLoggingService.logScheduleShiftCreate(createdShift, currentUser);
        } catch (logError) {
          console.error('Error logging schedule shift creation:', logError);
          // Don't throw here - the shift was created successfully
        }
      }

      return data.id;
    } catch (error) {
      console.error('Error in createScheduleShift:', error);
      throw error;
    }
  }

  async updateScheduleShift(shiftId: string, data: Partial<ScheduleShift>, performed_by?: string): Promise<void> {
    try {
      // Get current user for logging
      const current_user = performed_by || (await supabase.auth.getUser()).data.user?.id;
      
      // Get the current shift data for logging
      let before_data: Partial<ScheduleShift> | undefined;
      if (current_user) {
        try {
          const { data: currentShift } = await supabase
            .from('schedule_shifts')
            .select('*')
            .eq('id', shiftId)
            .single();
          if (currentShift) {
            before_data = convertDateFields(currentShift);
          }
        } catch (error) {
          console.error('Error fetching schedule shift for logging:', error);
        }
      }

      const updated_ata: any = {
        updated_at: DateTime.utc().toISO()
      };

      Object.entries(data).forEach(([key, value]) => {
        if (key === 'id' || key === 'created_at' || key === 'updated_at') return;

        if (key === 'date' && DateTime.isDateTime(value)) {
          updated_ata[key] = normalizeDateTo12PM(value).toISO();
        } else if (DateTime.isDateTime(value)) {
          updated_ata[key] = value.toISO();
        } else {
          updated_ata[key] = value;
        }
      });

      const { error } = await supabase
        .from('schedule_shifts')
        .update(updated_ata)
        .eq('id', shiftId);

      if (error) {
        console.error('Error updating schedule shift:', error);
        throw error;
      }

      // Log the schedule shift update if we have a user and before data
      if (current_user && before_data) {
        try {
          const after_data = { ...before_data, ...data };
          await SupabaseShiftLoggingService.logScheduleShiftUpdate(shiftId, { before: before_data, after: after_data }, current_user);
        } catch (logError) {
          console.error('Error logging schedule shift update:', logError);
          // Don't throw here - the shift was updated successfully
        }
      }
    } catch (error) {
      console.error('Error in updateScheduleShift:', error);
      throw error;
    }
  }

  async deleteScheduleShift(shiftId: string, performed_by?: string): Promise<void> {
    try {
      // Get current user for logging
      const current_user = performed_by || (await supabase.auth.getUser()).data.user?.id;
      
      // Get the current shift data for logging
      let shift_data: ScheduleShift | undefined;
      if (current_user) {
        try {
          const { data: current_shift } = await supabase
            .from('schedule_shifts')
            .select('*')
            .eq('id', shiftId)
            .single();
          if (current_shift) {
            shift_data = convertDateFields(current_shift);
          }
        } catch (error) {
          console.error('Error fetching schedule shift for logging:', error);
        }
      }

      const { error } = await supabase
        .from('schedule_shifts')
        .delete()
        .eq('id', shiftId);

      if (error) {
        console.error('Error deleting schedule shift:', error);
        throw error;
      }

      // Log the schedule shift deletion if we have a user and shift data
      if (current_user && shift_data) {
        try {
          await SupabaseShiftLoggingService.logScheduleShiftDelete(shift_data, current_user);
        } catch (logError) {
          console.error('Error logging schedule shift deletion:', logError);
          // Don't throw here - the shift was deleted successfully
        }
      }
    } catch (error) {
      console.error('Error in deleteScheduleShift:', error);
      throw error;
    }
  }

  async completeScheduleShift(shiftId: string, isOT: boolean): Promise<void> {
    try {
      await this.updateScheduleShift(shiftId, {
        completed_at: DateTime.utc(),
        is_ot: isOT
      });
    } catch (error) {
      console.error('Error in completeScheduleShift:', error);
      throw error;
    }
  }

  async getScheduleShiftsForMonth(month: DateTime, type?: ScheduleShiftType): Promise<ScheduleShift[]> {
    try {
      const monthStart = month.startOf('month');
      const monthEnd = month.endOf('month');

      const filters: ScheduleShiftFilter[] = [
        { field: 'date', operator: 'gte', value: monthStart.toISO() },
        { field: 'date', operator: 'lte', value: monthEnd.toISO() }
      ];

      if (type) {
        filters.push({ field: 'type', operator: 'eq', value: type });
      }

      return this.getScheduleShifts({ filters });
    } catch (error) {
      console.error('Error in getScheduleShiftsForMonth:', error);
      return [];
    }
  }

  async bulkCreateScheduleShifts(shifts: Omit<ScheduleShift, 'id' | 'created_at' | 'updated_at'>[]): Promise<string[]> {
    try {
      const now = DateTime.utc();
      const insertData = shifts.map(shift => ({
        user_id: shift.user_id,
        model_id: shift.model_id,
        type: shift.type,
        date: normalizeDateTo12PM(shift.date).toISO(),
        is_ot: shift.is_ot ?? false,
        completed_at: shift.completed_at?.toISO(),
        created_at: now.toISO(),
        updated_at: now.toISO()
      }));

      // Process in batches of 1000 (Supabase limit)
      const batchSize = 1000;
      const createdIds: string[] = [];
      
      for (let i = 0; i < insertData.length; i += batchSize) {
        const batch = insertData.slice(i, i + batchSize);
        
        const { data, error } = await supabase
          .from('schedule_shifts')
          .insert(batch)
          .select('id');

        if (error) {
          console.error('Error in bulk create batch:', error);
          throw error;
        }

        if (data) {
          createdIds.push(...data.map(item => item.id));
        }
      }

      return createdIds;
    } catch (error) {
      console.error('Error in bulkCreateScheduleShifts:', error);
      return [];
    }
  }
}

const instance = new SupabaseScheduleShiftService();

// React Query Hooks
export const useScheduleShifts = (params: ScheduleShiftParams = {}) => {
  return useQuery({
    queryKey: ['supabase-schedule-shifts', params],
    queryFn: () => instance.getScheduleShifts(params),
    staleTime: 5 * 60 * 1000,
    enabled: params.enabled !== false
  });
};

export const useCreateScheduleShift = () => {
  return useMutation({
    mutationFn: ({ shift, performed_by }: { shift: Omit<ScheduleShift, 'id' | 'created_at' | 'updated_at'>; performed_by?: string }) => 
      instance.createScheduleShift(shift, performed_by),
  });
};

export const useDeleteScheduleShift = () => {
  return useMutation({
    mutationFn: (params: string | { shift_id: string; type?: ScheduleShiftType; date?: DateTime; performed_by?: string }) => {
      const shift_id = typeof params === 'string' ? params : params.shift_id;
      const performed_by = typeof params === 'object' ? params.performed_by : undefined;
      return instance.deleteScheduleShift(shift_id, performed_by);
    },
  });
};

export const useUpdateScheduleShift = () => {
  return useMutation({
    mutationFn: ({ shift_id: shift_id, data, performed_by }: { shift_id: string; data: Partial<ScheduleShift>; date?: DateTime; type?: ScheduleShiftType; performed_by?: string }) =>
      instance.updateScheduleShift(shift_id, data, performed_by),
  });
};

export const useBulkCreateScheduleShifts = () => {
  return useMutation({
    mutationFn: (shifts: Omit<ScheduleShift, 'id' | 'created_at' | 'updated_at'>[]) =>
      instance.bulkCreateScheduleShifts(shifts),
  });
};

export const useScheduleShiftsForMonth = (month: DateTime, type?: ScheduleShiftType) => {
  const monthStart = month.startOf('month');
  const monthEnd = month.endOf('month');

  const filters: ScheduleShiftFilter[] = [
    { field: 'date', operator: 'gte', value: monthStart.toISO() },
    { field: 'date', operator: 'lte', value: monthEnd.toISO() }
  ];

  if (type) {
    filters.push({ field: 'type', operator: 'eq', value: type });
  }

  const params: ScheduleShiftParams = { filters };

  return useQuery({
    queryKey: ['supabase-schedule-shifts', params],
    queryFn: () => instance.getScheduleShiftsForMonth(month, type),
    staleTime: 60 * 1000,
  });
};

export const useCompleteScheduleShift = () => {
  return useMutation({
    mutationFn: ({ shift_id: shift_id, is_ot }: { shift_id: string; is_ot: boolean; date?: DateTime; type?: ScheduleShiftType }) =>
      instance.completeScheduleShift(shift_id, is_ot),
  });
};

export default instance;